import React, { useState, useEffect } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton, Tooltip, Snackbar, Chip, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ChatIcon from '@mui/icons-material/Chat';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LogoLoader from '../components/Common/LogoLoader';
import client from '../api/client';

const API = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace('/api/auth', '/api/ai-agents')
  : 'http://localhost:5000/api/ai-agents';

const TEMPLATE_ICONS = { sales: '🛒', support: '🎧', lead_qualifier: '📋', appointment: '📅', faq: '❓', custom: '🤖' };
const TEMPLATE_COLORS = { sales: '#f59e0b', support: '#6366f1', lead_qualifier: '#10b981', appointment: '#ec4899', faq: '#06b6d4', custom: '#8b5cf6' };

const AIAgents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const load = () => client.get(API).then(r => setAgents(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await client.post(API, { name: newName.trim() });
      setCreateOpen(false); setNewName('');
      navigate(`/app/ai-agents/${res.data._id}`);
    } finally { setCreating(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await client.delete(`${API}/${deleteTarget._id}`);
      setDeleteTarget(null);
      load();
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LogoLoader message="Loading agents..." />;

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 22, color: '#111827' }}>AI Agents</Typography>
          <Typography sx={{ fontSize: 13, color: '#6b7280' }}>Create intelligent AI agents that chat with your visitors</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
          Create Agent
        </Button>
      </Box>

      {agents.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SmartToyIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#374151', mb: 1 }}>No AI Agents yet</Typography>
          <Typography sx={{ color: '#9ca3af', mb: 3 }}>Create your first AI agent to start conversations</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            Create Your First Agent
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
          {agents.map(agent => {
            const color = TEMPLATE_COLORS[agent.template] || '#8b5cf6';
            return (
              <Box key={agent._id} sx={{ p: 2.5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)',
                border: '1px solid #e2e8f0', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                    {TEMPLATE_ICONS[agent.template] || '🤖'}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{agent.name}</Typography>
                    <Chip label={agent.template} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: `${color}15`, color }} />
                  </Box>
                  <Chip label={agent.status} size="small"
                    sx={{ height: 18, fontSize: 9, bgcolor: agent.status === 'active' ? '#ecfdf5' : '#f3f4f6', color: agent.status === 'active' ? '#059669' : '#6b7280' }} />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                  <Typography sx={{ fontSize: 11, color: '#6b7280' }}>💬 {agent.analytics?.totalChats || 0} chats</Typography>
                  <Typography sx={{ fontSize: 11, color: '#6b7280' }}>🎯 {agent.analytics?.leadsCollected || 0} leads</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => navigate(`/app/ai-agents/${agent._id}`)}
                    sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none', fontSize: 11, fontWeight: 600, color: '#6366f1', bgcolor: '#ede9fe' }}>Edit</Button>
                  <Button size="small" startIcon={<ChatIcon sx={{ fontSize: 14 }} />} onClick={() => navigate(`/app/ai-agents/${agent._id}/chats`)}
                    sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none', fontSize: 11, fontWeight: 600, color: '#059669', bgcolor: '#ecfdf5' }}>Chats</Button>
                  <IconButton size="small" onClick={() => setDeleteTarget(agent)} sx={{ color: '#ef4444' }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SmartToyIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>Create AI Agent</Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Give your agent a name</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <TextField autoFocus fullWidth size="small" value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="e.g. Sales Bot, Support Agent..." onKeyDown={e => e.key === 'Enter' && handleCreate()}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setCreateOpen(false)} fullWidth variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleCreate} fullWidth variant="contained" disabled={!newName.trim() || creating}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            {creating ? 'Creating...' : '✨ Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DeleteIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>Delete AI Agent</Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>This action cannot be undone</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
            <Typography sx={{ fontSize: 14, color: '#991b1b', lineHeight: 1.6 }}>
              Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>? All data, conversations, and settings will be permanently removed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} fullWidth variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', color: '#6b7280', borderColor: '#d1d5db' }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} disabled={deleting} fullWidth variant="contained"
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              '&:hover': { background: 'linear-gradient(135deg, #dc2626, #b91c1c)' } }}>
            {deleting ? 'Deleting...' : 'Delete Agent'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIAgents;
